import React from 'react'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
// import Link from '@material-ui/core/Link'
import { EmissionsForJourneysFragment } from './Sustainability.generated-types'

interface EmissionMessageProps {
  emissions: EmissionsForJourneysFragment[]
}

const messageCarbonComparisons = (emissionInKg: number): string => {
  const emissionInGram = emissionInKg * 1000

  if (emissionInKg < 1) {
    // Savings < 1kg - x plastic bags (10g) or x hours of Zoom calls (10g)
    return `${Math.round(emissionInGram / 10)} plastic bags or ${roundToTwo(emissionInGram / 10)} hours of Zoom calls`
  } else if (emissionInKg >= 1 && emissionInKg < 5) {
    // Savings between 1kg and 5kg - x disposable coffee cups (110g) or x hours of tv (200g)
    return `${Math.round(emissionInGram / 110)} disposable coffee cups or ${roundToTwo(emissionInGram / 200)} hours of tv`
  } else if (emissionInKg >= 5 && emissionInKg < 20) {
    // Savings between 5kg and 20kg - x litre water bottles (400g) or x kg of rubbish to landfill (590g)
    return `${Math.round(emissionInGram / 400)} litre water bottles or ${roundToTwo(emissionInGram / 590)} kg of rubbish to landfill`
  } else {
    // Savings > 1.5kg - x loads of laundry tumble dried (1.5kg)
    return `${Math.round(emissionInKg / 1.5)} loads of laundry tumble dried`
  }
}

const roundToTwo = (num): number => {
  return Math.round(num * 100) / 100
}

const EmissionMessageContent = (emissions): string => {
  const hasJourneyByPlane = emissions.some(e => e.journeyType === 'PLANE')
  const hasJourneysByCarOrPlane = emissions.some(e => e.journey.journeyType === 'DRIVING' || e.journey.journeyType === 'PLANE')

  const journeysByPlaneLess1000 = emissions.filter(e => e.journey.journeyType === 'PLANE' && e.travelDistance < 1000)
  const journeysByCar = emissions.filter(e => e.journey.journeyType === 'DRIVING')

  const journeysByTrainOrBusLess3Km = emissions.filter(e => e.travelDistance < 3 && ['BUS', 'TRAIN'].includes(e.journey.journeyType))
  const emissionPerKMByTrain = 0.03549

  // If there are 1 or more plane journeys <1000km,
  // show the difference between total C02e for all plane journeys <1000km and total C02e for those distances via train.

  if (journeysByPlaneLess1000.length) {
    const emissionsBefore = journeysByPlaneLess1000.reduce((n: number, { calculatedEmissions }: { calculatedEmissions: number }) => n + calculatedEmissions, 0)
    const totalTravelDistance = journeysByPlaneLess1000.reduce((n: number, { travelDistance, journey }) => n + (travelDistance * journey.numberOfPeople), 0)
    const emissionsAfter = emissionPerKMByTrain * totalTravelDistance
    const emissionsReduction = roundToTwo(emissionsBefore - emissionsAfter)

    return `Taking the train instead of plane would save ${emissionsReduction}kg of CO2e 
            - that’s the equivalent of ${messageCarbonComparisons(emissionsReduction)}`
  }

  // If there are no plane journeys and there are 1 or more car journeys,
  // show the difference between total C02e for all car journeys, and total C02e for those distances via train.

  if (!hasJourneyByPlane && journeysByCar.length) {
    const emissionsBefore = journeysByCar.reduce((n: number, { calculatedEmissions }: { calculatedEmissions: number }) => n + calculatedEmissions, 0)
    const totalTravelDistance = journeysByCar.reduce((n: number, { travelDistance, journey }) => n + (travelDistance * journey.numberOfPeople), 0)
    const emissionsAfter = emissionPerKMByTrain * totalTravelDistance
    const emissionsReduction = roundToTwo(emissionsBefore - emissionsAfter)

    return `Taking the train instead of car or would save ${emissionsReduction}kg of CO2e 
            - that’s the equivalent of ${messageCarbonComparisons(emissionsReduction)}`
  }

  // If there are no car or plane journeys and there are 1 or more train or bus journeys of less than 3km,
  // show the difference between total emissions for train journeys < 3km and walking (0) AND the walking time (based on 4.5km/h walking speed).
  // (not show the walking time if it’s >1 journey)

  if (!hasJourneysByCarOrPlane && journeysByTrainOrBusLess3Km.length) {
    const emissionsBefore = journeysByTrainOrBusLess3Km.reduce((n: number, { calculatedEmissions }: { calculatedEmissions: number }) => n + calculatedEmissions, 0)
    const emissionsReduction = roundToTwo(emissionsBefore)

    if (journeysByTrainOrBusLess3Km.length === 1) {
      const totalTravelDistance = journeysByTrainOrBusLess3Km.reduce((n: number, { travelDistance }: { travelDistance: number }) => n + (travelDistance), 0)
      const walkingHours = roundToTwo(totalTravelDistance / 4.5)

      return `Taking ${walkingHours} hours of
              walk instead of train would save ${emissionsReduction}kg of CO2e
              - that’s the equivalent of ${messageCarbonComparisons(emissionsReduction)}`
    } else {
      return `Taking walk instead of train would save ${emissionsReduction}kg of CO2e
                - that’s the equivalent of ${messageCarbonComparisons(emissionsReduction)}`
    }
  }

  return null
}

export const EmissionMessage: React.FC<EmissionMessageProps> = ({ emissions }) => {
  const content = EmissionMessageContent(emissions)

  return (
    <>
      { content
        ? <Card style={{ border: '1px solid #187759', borderRadius: 4, backgroundColor: '#fafafa', boxShadow: 'none', marginTop: 24 }}>
          <CardContent style={{ padding: 16 }}>
            {content}
          </CardContent>
        </Card> : <></>
      }
    </>
  )
}
